import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Percent, Save, ArrowLeft, Calendar, ShieldCheck, ListFilter, RefreshCw, ToggleLeft, ToggleRight } from 'lucide-react';
import { getTasaById, createTasa, updateTasa } from '../services/tasaService';
import API_BASE_URL from '../Config';
import toast, { Toaster } from 'react-hot-toast';

const TasaForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;
    const token = localStorage.getItem('token');
    const [loading, setLoading] = useState(false);
    const [tiposCredito, setTiposCredito] = useState([]);
    const [tiposCuenta, setTiposCuenta] = useState([]);

    const [form, setForm] = useState({
        tipoCredito: { id: '' },
        tipoCuenta: { id: '' },
        tan: 0,
        tae: 0,
        fechaInicioVigencia: new Date().toISOString().split('T')[0],
        fechaFinVigencia: '',
        esActiva: true
    });

    useEffect(() => {
        cargarCatalogos();
        if (isEditMode) {
            cargarTasa();
        }
    }, [id]);

    const cargarCatalogos = async () => {
        try {
            console.log('Cargando catálogos en TasaForm...');
            const [respCredito, respCuenta] = await Promise.all([
                fetch(`${API_BASE_URL}/tipos-credito`, { headers: { Authorization: `Bearer ${token}` } }).then(r => {
                    console.log('Respuesta tipos-credito:', r.status);
                    return r.json();
                }),
                fetch(`${API_BASE_URL}/tipos-cuenta`, { headers: { Authorization: `Bearer ${token}` } }).then(r => {
                    console.log('Respuesta tipos-cuenta:', r.status);
                    return r.json();
                })
            ]);
            console.log('Catálogos cargados:', { tiposCredito: respCredito.length, tiposCuenta: respCuenta.length });
            setTiposCredito(respCredito);
            setTiposCuenta(respCuenta);
        } catch (error) {
            console.error('Error en cargarCatalogos:', error);
            toast.error('Error al cargar catálogos de productos');
        }
    };

    const cargarTasa = async () => {
        try {
            setLoading(true);
            console.log('Cargando datos de tasa ID:', id);
            const data = await getTasaById(token, id);
            console.log('Datos de tasa cargados:', data);
            setForm({
                ...data,
                tipoCredito: data.tipoCredito || { id: '' },
                tipoCuenta: data.tipoCuenta || { id: '' }
            });
        } catch (error) {
            console.error('Error en cargarTasa:', error);
            toast.error('Error al cargar los datos de la tasa');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.tipoCredito.id && !form.tipoCuenta.id) {
            toast.error('Debe seleccionar un Tipo de Crédito o un Tipo de Cuenta');
            return;
        }

        try {
            setLoading(true);
            const payload = {
                ...form,
                tipoCredito: form.tipoCredito.id ? { id: parseInt(form.tipoCredito.id) } : null,
                tipoCuenta: form.tipoCuenta.id ? { id: parseInt(form.tipoCuenta.id) } : null,
                tan: parseFloat(form.tan),
                tae: parseFloat(form.tae)
            };

            if (isEditMode) {
                await updateTasa(token, id, payload);
                toast.success('Tasa actualizada correctamente');
            } else {
                await createTasa(token, payload);
                toast.success('Nueva tasa registrada');
            }
            setTimeout(() => navigate('/parametrizacion/tasas'), 1000);
        } catch (error) {
            toast.error('Error al guardar la tasa');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto mb-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <Toaster position="top-right" />

            {/* Header Form Enterprise */}
            <div className="bg-slate-700 p-4 shadow-lg flex justify-between items-center text-white rounded-t-sm border-b border-slate-600">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/parametrizacion/tasas')}
                        className="p-2 hover:bg-slate-600 rounded-full transition-all group"
                        title="Volver al listado"
                    >
                        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                    </button>
                    <div className="h-10 w-[2px] bg-slate-500 mx-1"></div>
                    <div>
                        <h1 className="text-xl font-bold uppercase tracking-tight flex items-center gap-2">
                            <ShieldCheck className="w-6 h-6 text-blue-400" />
                            {isEditMode ? 'Edición de Tasa de Interés' : 'Registro de Nueva Tasa'}
                        </h1>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest italic">Mantenimiento de Parámetros de Rentabilidad</p>
                    </div>
                </div>
                <div className="hidden md:block bg-slate-800 px-4 py-1.5 text-[11px] font-black uppercase border border-slate-600 shadow-inner italic rounded-sm">
                    {isEditMode ? `TASA-ID: ${id}` : 'NUEVA DEFINICIÓN'}
                </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white border-x border-b border-slate-200 shadow-2xl">
                <div className="grid grid-cols-1 lg:grid-cols-2">

                    {/* SECCIÓN 1: Aplicabilidad */}
                    <div className="p-8 border-r border-slate-100 bg-gray-50/30">
                        <div className="flex items-center gap-2 mb-6 border-b border-slate-200 pb-3">
                            <ListFilter className="w-5 h-5 text-blue-600" />
                            <h3 className="text-xs font-black text-gray-700 uppercase tracking-widest">Aplicabilidad del Producto</h3>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-[11px] font-bold text-gray-600 mb-2 uppercase tracking-tighter">Tipo de Crédito (Cartera Activa)</label>
                                <select
                                    disabled={!!form.tipoCuenta.id}
                                    className="w-full px-4 py-3 border-2 border-slate-200 focus:border-blue-500 outline-none transition-all font-bold text-sm bg-white uppercase"
                                    value={form.tipoCredito.id}
                                    onChange={(e) => setForm({ ...form, tipoCredito: { id: e.target.value }, tipoCuenta: { id: '' } })}
                                >
                                    <option value="">-- SELECCIONE CRÉDITO --</option>
                                    {tiposCredito.map(tc => <option key={tc.id} value={tc.id}>{tc.descripcion}</option>)}
                                </select>
                            </div>

                            <div className="relative py-4 flex items-center">
                                <div className="flex-grow border-t border-gray-300"></div>
                                <span className="flex-shrink mx-4 text-[10px] font-black text-gray-400 uppercase italic">O BIEN</span>
                                <div className="flex-grow border-t border-gray-300"></div>
                            </div>

                            <div>
                                <label className="block text-[11px] font-bold text-gray-600 mb-2 uppercase tracking-tighter">Tipo de Cuenta (Cartera Pasiva)</label>
                                <select
                                    disabled={!!form.tipoCredito.id}
                                    className="w-full px-4 py-3 border-2 border-slate-200 focus:border-blue-500 outline-none transition-all font-bold text-sm bg-white uppercase"
                                    value={form.tipoCuenta.id}
                                    onChange={(e) => setForm({ ...form, tipoCuenta: { id: e.target.value }, tipoCredito: { id: '' } })}
                                >
                                    <option value="">-- SELECCIONE CUENTA --</option>
                                    {tiposCuenta.map(tc => <option key={tc.id} value={tc.id}>{tc.descripcion}</option>)}
                                </select>
                            </div>

                            <div className="pt-6">
                                <label className="flex items-center gap-3 cursor-pointer group">
                                    <div
                                        onClick={() => setForm({ ...form, esActiva: !form.esActiva })}
                                        className="transition-all active:scale-90"
                                    >
                                        {form.esActiva ?
                                            <ToggleRight className="w-10 h-10 text-blue-600" /> :
                                            <ToggleLeft className="w-10 h-10 text-gray-300" />
                                        }
                                    </div>
                                    <div className="flex flex-col">
                                        <span className={`text-xs font-black uppercase tracking-widest ${form.esActiva ? 'text-blue-700' : 'text-gray-400'}`}>
                                            Estado Visibilidad: {form.esActiva ? 'ACTIVA' : 'INACTIVA'}
                                        </span>
                                        <span className="text-[9px] text-gray-400 font-bold italic uppercase">Define si la tasa está disponible para nuevos cálculos</span>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* SECCIÓN 2: Valores y Vigencia */}
                    <div className="p-8">
                        <div className="flex items-center gap-2 mb-6 border-b border-slate-200 pb-3">
                            <Percent className="w-5 h-5 text-blue-600" />
                            <h3 className="text-xs font-black text-gray-700 uppercase tracking-widest">Parámetros y Vigencias</h3>
                        </div>

                        <div className="grid grid-cols-2 gap-6 mb-8">
                            <div className="bg-slate-50 p-4 border border-slate-200 shadow-inner">
                                <label className="block text-[10px] font-black text-slate-500 mb-1.5 uppercase">Tasa Nom. Anual (TAN %)</label>
                                <div className="relative">
                                    <input
                                        type="number" step="0.01" required
                                        className="w-full pl-4 pr-10 py-3 border-2 border-slate-300 focus:border-blue-500 outline-none font-black text-lg bg-white"
                                        value={form.tan}
                                        onChange={(e) => setForm({ ...form, tan: e.target.value })}
                                    />
                                    <div className="absolute right-3 top-3.5 text-slate-400 font-bold text-sm">%</div>
                                </div>
                            </div>
                            <div className="bg-blue-50 p-4 border border-blue-100 shadow-inner">
                                <label className="block text-[10px] font-black text-blue-600 mb-1.5 uppercase tracking-tighter">Tasa Efec. Anual (TAE %)</label>
                                <div className="relative">
                                    <input
                                        type="number" step="0.01" required
                                        className="w-full pl-4 pr-10 py-3 border-2 border-blue-200 focus:border-blue-500 outline-none font-black text-lg bg-white text-blue-800 shadow-sm"
                                        value={form.tae}
                                        onChange={(e) => setForm({ ...form, tae: e.target.value })}
                                    />
                                    <div className="absolute right-3 top-3.5 text-blue-400 font-bold text-sm">%</div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-[11px] font-bold text-gray-600 mb-2 uppercase flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-gray-400" /> Inicio de Vigencia
                                </label>
                                <input
                                    type="date" required
                                    className="w-full px-4 py-3 border-2 border-slate-200 focus:border-blue-500 outline-none font-bold uppercase"
                                    value={form.fechaInicioVigencia}
                                    onChange={(e) => setForm({ ...form, fechaInicioVigencia: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold text-gray-600 mb-2 uppercase flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-gray-400 opacity-40" /> Fin de Vigencia (Opcional)
                                </label>
                                <input
                                    type="date"
                                    className="w-full px-4 py-3 border-2 border-slate-200 focus:border-blue-500 outline-none font-bold bg-slate-50/50 uppercase"
                                    value={form.fechaFinVigencia}
                                    onChange={(e) => setForm({ ...form, fechaFinVigencia: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer de Acciones Enterprise */}
                <div className="p-6 bg-slate-50 border-t border-slate-200 flex justify-between items-center px-10">
                    <div className="text-[10px] text-slate-400 font-bold uppercase italic max-w-sm">
                        * Al guardar este registro, los nuevos créditos o cuentas utilizarán estos parámetros según la fecha de vigencia establecida.
                    </div>
                    <div className="flex gap-4">
                        <button
                            type="button"
                            onClick={() => navigate('/parametrizacion/tasas')}
                            className="px-6 py-2.5 border-2 border-slate-300 text-slate-500 font-black text-xs hover:bg-slate-100 transition-all uppercase rounded-sm"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center gap-2 bg-blue-600 text-white px-8 py-2.5 rounded-sm text-xs font-black hover:bg-blue-700 transition-all shadow-lg active:scale-95 disabled:opacity-50 uppercase"
                        >
                            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            {isEditMode ? 'Actualizar Tasa' : 'Confirmar y Guardar'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default TasaForm;
